/**
 * Route-level redirects for nav entries without a page of their own —
 * V98-3-1 (dead nav targets fixed by routing them to real pages).
 */
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader, Spinner } from '@aigovops/design-system';
import { ResourceReveal } from '../components/sweep/ResourceReveal';
import './programs.css';
import { programsWhere } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { usePrograms } from '../lib/workspace-navigation';

/**
 * /w/:slug/app/assessor/workpaper (assessor nav "Workpapers") → the
 * workpaper of the engagement this assessor session is scoped to; with no
 * engagement in scope, the cross-workspace assessor inbox.
 */
export function AssessorWorkpaperIndex() {
  const { workspace, scopeBar } = useWorkspace();
  if (scopeBar?.engagement_id) {
    return <Navigate to={`/w/${workspace.slug}/app/assessor/workpaper/${encodeURIComponent(scopeBar.engagement_id)}`} replace />;
  }
  return <Navigate to="/assessor" replace />;
}

/** Retired path → its new home inside the workspace, keeping query + hash. */
export function WorkspaceRedirect({ to }: { to: string }) {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  if (!slug) return <Navigate to="/" replace />;
  return <Navigate to={`/w/${encodeURIComponent(slug)}/app/${to}${location.search ?? ''}${location.hash ?? ''}`} replace />;
}

/**
 * Landing order for the Boundary link (ADR-0050): programs whose boundary
 * carries provider coverage (manifest `coverage.documentKinds`) first, then
 * the rest, each in manifest order — SOC 2, FedRAMP, ISO 42001, then AI
 * Regulatory.
 */
export const BOUNDARY_LANDING_ORDER: readonly string[] = [
  ...programsWhere((m) => m.coverage.documentKinds.length > 0),
  ...programsWhere((m) => m.coverage.documentKinds.length === 0),
].map((m) => m.key);

/**
 * /w/:slug/app/boundary (sidebar "Boundary") → the Scope tab of the first
 * program with a boundary (BOUNDARY_LANDING_ORDER); with no program yet, the
 * Programs page (which offers to add one).
 */
export function BoundaryRedirect() {
  const { workspace, role } = useWorkspace();
  const programs = usePrograms(workspace.slug);
  const [sp] = useSearchParams();
  if (programs.isLoading) return <Spinner label="Loading boundary" />;
  const keys = new Set<string>((programs.data?.programs ?? []).map((p) => p.program));
  const base = `/w/${workspace.slug}/app`;
  // V109-5-2 (PD-6): the inventory after a sweep is reachable for EVERY workspace — inside the first program
  // whose Scope has the inventory view (manifest order), else on its own page here.
  if (sp.get('view') === 'inventory') {
    const guided = programsWhere((m) => m.features.transitionGuidance === true).map((m) => m.key).find((k) => keys.has(k));
    if (guided) return <Navigate to={`${base}/programs/${guided}/scope?view=inventory`} replace />;
    return (
      <div className="gp-program-shell">
        <PageHeader variant="app" title="Everything the sweep found" headingId="boundary-h1" />
        <ResourceReveal slug={workspace.slug} prefix={base} program={null} canManage={role === 'owner' || role === 'admin'} boundaryHref={`${base}/programs`} />
      </div>
    );
  }
  const first = BOUNDARY_LANDING_ORDER.find((k) => keys.has(k));
  return <Navigate to={first ? `${base}/programs/${first}/scope` : `${base}/programs`} replace />;
}
